import {Action, Alert, IconButton, openDeleteDialog, showNotification} from 'platform/components';
import {Show} from 'platform/foundation';

import {HTMLAttributes} from 'react';

import {PurchasePriceRuleResponseBody, useDeletePurchasePriceRuleMutation} from '@dms/api/sales';
import {DraggableCard} from '@dms/features/dnd';
import {i18n} from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import {PurchasePriceRuleScopeLine} from './PurchasePriceRuleScopeLine';

const FIRST_POSITION = 1;

interface PurchasePriceRuleCardProps {
  /** A custom (non-system) rule. */
  rule: PurchasePriceRuleResponseBody;
  /** All rules in the displayed order, used to resolve the rule that shadows this one. */
  rules: PurchasePriceRuleResponseBody[];
  canManage: boolean;
  handleProps?: HTMLAttributes<HTMLDivElement>;
}

export function PurchasePriceRuleCard(props: PurchasePriceRuleCardProps) {
  const navigate = useNavigate();
  const [deletePurchasePriceRule] = useDeletePurchasePriceRuleMutation();

  const shadowingRule = props.rules.find((rule) => rule.id === props.rule.shadowedByRuleId);
  const ruleTestId = testIds.settings.purchasePrice(`rule-${props.rule.id}`);

  const handleOpenDetail = () =>
    navigate(composePath(settingsRoutes.purchasePriceRuleDetail, {params: {id: props.rule.id}}));

  const handleDelete = () =>
    openDeleteDialog({
      text: i18n.t('page.settings.purchasePrice.marginRules.deleteDialogText', {
        name: props.rule.name,
      }),
      'data-testid': testIds.settings.purchasePrice('deleteRuleDialog'),
      onConfirm: () =>
        deletePurchasePriceRule({id: props.rule.id})
          .unwrap()
          .then(() =>
            showNotification.success(
              i18n.t('page.settings.purchasePrice.marginRules.deletedNotification', {
                name: props.rule.name,
              })
            )
          )
          .catch(handleApiError),
    });

  const actions = buildArray<Action>()
    .when(props.canManage, {
      type: 'custom',
      component: (
        <IconButton
          icon="action/delete"
          priority="tertiary"
          severity="danger"
          aria-label={i18n.t('general.actions.delete')}
          onClick={handleDelete}
          data-testid={`${ruleTestId}-delete`}
        />
      ),
    })
    .when(props.canManage, {
      type: 'button',
      variant: 'outlined',
      leftIcon: 'image/edit',
      title: i18n.t('general.actions.edit'),
      onClick: handleOpenDetail,
      'data-testid': `${ruleTestId}-edit`,
    })
    .whenNot(props.canManage, {
      type: 'button',
      variant: 'outlined',
      title: i18n.t('general.actions.view'),
      onClick: handleOpenDetail,
      'data-testid': `${ruleTestId}-view`,
    });

  return (
    <DraggableCard
      title={props.rule.name}
      handleProps={props.handleProps}
      variant="inlineGrey"
      actions={actions}
      shouldKeepActionsInline
      data-testid={ruleTestId}
      additionalContent={
        <Show when={props.rule.scope}>
          {(scope) => (
            <PurchasePriceRuleScopeLine scope={scope} data-testid={`${ruleTestId}-scope`} />
          )}
        </Show>
      }
      footer={
        <Show when={shadowingRule}>
          {(shadowingRule) => (
            <Alert
              type="inline"
              variant="warning"
              message={i18n.t('page.settings.purchasePrice.marginRules.shadowedWarning', {
                name: shadowingRule.name,
                position: props.rules.indexOf(shadowingRule) + FIRST_POSITION,
              })}
              data-testid={`${ruleTestId}-shadowedWarning`}
            />
          )}
        </Show>
      }
    />
  );
}
